import { type ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Base } from '@itsrever/design-system'
import { PbECommerceSettingsPlatformEnum } from '@itsrever/rever-api'
import type { ReturnProcess } from '@/domain/returns'
import { useCalculateRefundAmount } from '@/hooks/use-calculate-refund-amount'
import { useProcessAvailableRefundMethods } from '@/hooks/use-process-available-refund-methods'
import { formatCurrency } from '@/utils'
import { EditProcessConfirmation } from '../edit-process-confirmation'
import { RefundMethodSelect } from './refund-method-select'
import {
  currentMethod,
  isExchangeChangeBlocked,
  keepsExchangeOnly
} from './refund-method-options'

const {
  Callout,
  Button,
  Textarea,
  Input,
  DialogRoot,
  DialogPopup,
  DialogBackdrop,
  DialogPortal,
  DialogFooter
} = Base

const isValidAmount = (amount: string) => {
  const numberRegex = /^\d+(?:\.\d+)?$/

  if (!numberRegex.test(amount)) {
    return false
  }

  const parsed = Number(amount)

  return !isNaN(parsed) && isFinite(parsed)
}

export type EditProcessFormData = {
  method: string
  amount: string
  currency: string
  reason: string
}

type FormErrorState = {
  [K in keyof EditProcessFormData]?: { message: string }
}

interface EditProcessFormProps {
  onClose: () => void
  refundAmount: {
    amount: string
    currency: string
  }
  process: ReturnProcess
}

export const EditProcessForm = ({
  onClose,
  refundAmount,
  process
}: EditProcessFormProps) => {
  const { t } = useTranslation()

  const { id: processId } = useParams()

  const [openConfirmation, setOpenConfirmation] = useState(false)
  const [formError, setFormError] = useState<FormErrorState>({})
  const [form, setForm] = useState<EditProcessFormData>({
    method: '',
    amount: refundAmount.amount,
    currency: refundAmount.currency,
    reason: ''
  })

  const { data: refundMethods, status: refundMethodStatus } =
    useProcessAvailableRefundMethods({
      processId
    })

  const {
    mutate: calculateRefundAmount,
    status: calculateStatus,
    data
  } = useCalculateRefundAmount()

  const updateFormField = (field: keyof EditProcessFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const fromMethod = currentMethod(refundMethods)
  const exchangeBlocked = isExchangeChangeBlocked(
    refundMethods,
    process.isOnlyExchange
  )

  const onRefundMethodChange = (method: string) => {
    updateFormField('method', method)

    if (processId && method !== fromMethod) {
      calculateRefundAmount(
        {
          processId
        },
        {
          onSuccess: (response) => {
            if (formError.amount) {
              setFormError((prev) => {
                const errors = { ...prev }
                delete errors.amount
                return errors
              })
            }

            updateFormField(
              'amount',
              (
                parseInt(response.amount ?? '0') /
                (response.money_format?.amount_multiplied_by ?? 100)
              ).toFixed(2)
            )
          }
        }
      )

      return
    }

    if (formError.amount) {
      setFormError((prev) => {
        const errors = { ...prev }
        delete errors.amount
        return errors
      })
    }

    updateFormField('amount', refundAmount.amount)
  }

  const onEditRefund = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (formError.amount) {
      return
    }

    if (form.reason.length === 0) {
      setFormError((prev) => ({
        ...prev,
        reason: {
          message: 'process_details_page.error_edited_reason'
        }
      }))

      return
    }

    setOpenConfirmation(true)
  }

  const hint = () => {
    if (!form.amount || !isValidAmount(form.amount)) {
      return ''
    }

    const hasChangedRefundMethod = form.method && form.method !== fromMethod

    const originalRefundAmount =
      hasChangedRefundMethod && data
        ? (
            parseInt(data.amount ?? '0') /
            (data.money_format?.amount_multiplied_by ?? 100)
          ).toFixed(2)
        : refundAmount.amount

    const diff = Number(originalRefundAmount) - Number(form.amount)

    if (!diff) return ''

    const formatted = formatCurrency(
      Math.abs(diff).toFixed(2),
      refundAmount.currency
    )

    return `${t('refund_island.refunding')} ${formatted} ${t(`refund_island.${diff < 0 ? 'more' : 'less'}`)}`
  }

  const refundAmountHint = hint()

  const isDisabled =
    calculateStatus === 'pending' ||
    refundMethodStatus === 'pending' ||
    exchangeBlocked ||
    keepsExchangeOnly(refundMethods, form, process.isOnlyExchange)

  // The warning is about gift cards and promo codes; an exchange has neither to check.
  const shouldDisplayWarning =
    fromMethod &&
    !refundMethods?.from_exchange &&
    form.method &&
    process.platform !== PbECommerceSettingsPlatformEnum.Salesforce &&
    process.platform !== PbECommerceSettingsPlatformEnum.Shopify &&
    fromMethod !== form.method

  useEffect(() => {
    const method = currentMethod(refundMethods)
    if (method !== undefined) {
      updateFormField('method', method)
    }
  }, [refundMethods])

  return (
    <>
      <form className="space-y-6" onSubmit={onEditRefund}>
        <div className="space-y-3">
          <div className="space-y-2.5">
            {exchangeBlocked ? (
              <p className="text-sm text-neutral-600">
                {t('process_details_page.exchange_change_blocked')}
              </p>
            ) : (
              <RefundMethodSelect
                refundMethods={refundMethods}
                onUpdateRefundMethod={onRefundMethodChange}
                value={form.method}
              />
            )}
            {shouldDisplayWarning ? (
              <Callout>
                <p>{t('process_details_page.refund_method_warning')}</p>
              </Callout>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <label className="inline-block text-sm" htmlFor="refund-amount">
              <Trans
                i18nKey="process_details_page.refund_amount_label"
                values={{ currency: refundAmount.currency }}
              />
            </label>
            <div className="flex flex-col gap-2">
              <Input
                autoComplete="off"
                value={form.amount}
                disabled={isDisabled}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  updateFormField('amount', e.target.value)

                  if (!isValidAmount(e.target.value)) {
                    setFormError((prev) => ({
                      ...prev,
                      amount: {
                        message: 'process_details_page.error_edited_amount'
                      }
                    }))

                    return
                  }

                  if (formError.amount) {
                    setFormError((prev) => {
                      const errors = { ...prev }
                      delete errors.amount
                      return errors
                    })
                  }
                }}
                name="refund-amount"
                id="refund-amount"
                placeholder="0.00"
              />
              {!isDisabled &&
                (formError.amount ? (
                  <span className="pl-1 text-xs text-red-500">
                    {t(formError.amount.message)}
                  </span>
                ) : refundAmountHint ? (
                  <span className="pl-1 text-xs text-neutral-600">
                    {refundAmountHint}
                  </span>
                ) : null)}
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="inline-block text-sm" htmlFor="reason">
              {t('process_details_page.reason_for_adjustment_label')}
            </label>
            <div className="flex flex-col gap-2">
              <Textarea
                onChange={(e) => {
                  updateFormField('reason', e.target.value)

                  if (e.currentTarget.value.length === 0) {
                    setFormError((prev) => ({
                      ...prev,
                      reason: {
                        message: 'process_details_page.error_edited_reason'
                      }
                    }))
                  } else {
                    if (formError.reason) {
                      setFormError((prev) => {
                        const errors = { ...prev }
                        delete errors.reason
                        return errors
                      })
                    }
                  }
                }}
                maxLength={256}
                className="resize-none font-sans"
                name="reason"
                id="reason"
                placeholder={t('process_details_page.reason_placeholder')}
              />
              {formError.reason ? (
                <span className="pl-1 text-xs leading-none text-red-500">
                  {t(formError.reason?.message)}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('process_details_page.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isDisabled}>
            {t('process_details_page.edit_refund')}
          </Button>
        </DialogFooter>
      </form>
      <DialogRoot open={openConfirmation} onOpenChange={setOpenConfirmation}>
        <DialogPortal>
          <DialogBackdrop forceRender className="opacity-40" />
          <DialogPopup>
            <EditProcessConfirmation
              onClose={() => setOpenConfirmation(false)}
              data={form}
              refundMethods={refundMethods}
            />
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>
    </>
  )
}
